import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Database } from '../../../lib/database.types';
import { softDelete } from '../../../lib/softDelete';
import { supabase } from '../../../lib/supabase';
import type { LeadDisplayStatus, LeadFilters, LeadInput, LeadRow, LeadSourceRow } from './leads.schema';

export const leadsQueryKey = (orgId: string | null, filters?: LeadFilters) => ['leads', orgId, filters] as const;
export const leadSourcesQueryKey = (orgId: string | null) => ['lead-sources', orgId] as const;

function emptyToNull(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

function scoreOrNull(score: number | undefined): number | null {
  return typeof score === 'number' ? score : null;
}

function toIso(value: string): string {
  return new Date(value).toISOString();
}

function sanitizeSearchTerm(term: string): string {
  return term.replace(/[%(),]/g, '').trim();
}

function buildNullableFilter(column: 'source_id' | 'score_class', values: (string | null)[]): string | null {
  const withValue = values.filter((value): value is string => value !== null);
  const hasNull = values.includes(null);
  if (withValue.length > 0 && hasNull) return `${column}.in.(${withValue.join(',')}),${column}.is.null`;
  return null;
}

async function fetchLeads(orgId: string, filters: LeadFilters): Promise<LeadRow[]> {
  let query = supabase
    .from('leads')
    .select('id, full_name, company_name, email, phone, source_id, interest, score, score_class, score_source, status, owned_by, received_at, created_at, updated_at, notes')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('received_at', { ascending: false });

  const search = sanitizeSearchTerm(filters.search);
  if (search) {
    query = query.or(`full_name.ilike.%${search}%,company_name.ilike.%${search}%,email.ilike.%${search}%`);
  }

  if (filters.statuses.length > 0) {
    query = query.in('status', filters.statuses);
  }

  if (filters.sourceIds.length > 0) {
    const withId = filters.sourceIds.filter((value): value is string => value !== null);
    const hasNull = filters.sourceIds.includes(null);
    const nullableFilter = buildNullableFilter('source_id', filters.sourceIds);
    if (nullableFilter) query = query.or(nullableFilter);
    else if (withId.length > 0) query = query.in('source_id', withId);
    else if (hasNull) query = query.is('source_id', null);
  }

  if (filters.scoreClasses.length > 0) {
    const withClass = filters.scoreClasses.filter((value): value is NonNullable<typeof value> => value !== null);
    const hasNull = filters.scoreClasses.includes(null);
    const nullableFilter = buildNullableFilter('score_class', filters.scoreClasses);
    if (nullableFilter) query = query.or(nullableFilter);
    else if (withClass.length > 0) query = query.in('score_class', withClass);
    else if (hasNull) query = query.is('score_class', null);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

async function fetchLeadSources(orgId: string): Promise<LeadSourceRow[]> {
  const { data, error } = await supabase
    .from('lead_sources')
    .select('id, name, slug, color')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return data;
}

export function useLeads(orgId: string | null, filters: LeadFilters) {
  return useQuery({
    queryKey: leadsQueryKey(orgId, filters),
    queryFn: () => {
      if (!orgId) return Promise.resolve([]);
      return fetchLeads(orgId, filters);
    },
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
  });
}

// ─── single lead fetch (detail page) ─────────────────────────────────────

export type LeadDetailRow = Database['public']['Tables']['leads']['Row'];

async function fetchLead(orgId: string, leadId: string): Promise<LeadDetailRow | null> {
  const { data, error } = await supabase
    .from('leads')
    .select('*')
    .eq('organization_id', orgId)
    .eq('id', leadId)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export function useLead(orgId: string | null, leadId: string | null) {
  return useQuery({
    queryKey: ['lead', orgId, leadId] as const,
    queryFn: () => {
      if (!orgId || !leadId) return Promise.resolve<LeadDetailRow | null>(null);
      return fetchLead(orgId, leadId);
    },
    enabled: Boolean(orgId && leadId),
    staleTime: 15 * 1000,
  });
}

export function useLeadSources(orgId: string | null) {
  return useQuery({
    queryKey: leadSourcesQueryKey(orgId),
    queryFn: () => {
      if (!orgId) return Promise.resolve([]);
      return fetchLeadSources(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 5 * 60 * 1000,
  });
}

type LeadInsert = Database['public']['Tables']['leads']['Insert'];
type LeadUpdate = Database['public']['Tables']['leads']['Update'];

function toPayload(values: LeadInput): Omit<LeadInsert, 'organization_id' | 'owned_by' | 'created_by'> {
  const score = scoreOrNull(values.score);
  return {
    full_name: values.full_name.trim(),
    company_name: emptyToNull(values.company_name),
    email: emptyToNull(values.email),
    phone: emptyToNull(values.phone),
    source_id: emptyToNull(values.source_id),
    interest: emptyToNull(values.interest),
    score,
    score_source: score === null ? null : 'manual',
    status: values.status,
    received_at: toIso(values.received_at),
    notes: emptyToNull(values.notes),
  };
}

export function useUpsertLead(orgId: string | null, userId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: LeadInput }) => {
      if (!orgId || !userId) throw new Error('Missing lead context');

      if (id) {
        const payload: LeadUpdate = toPayload(values);
        const { error } = await supabase.from('leads').update(payload).eq('id', id);
        if (error) throw error;
        return;
      }

      const payload: LeadInsert = {
        ...toPayload(values),
        organization_id: orgId,
        owned_by: userId,
        created_by: userId,
      };
      const { error } = await supabase.from('leads').insert(payload);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
    },
  });
}

export function useSoftDeleteLead(orgId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await softDelete('leads', id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
    },
  });
}

export function useBulkUpdateLeadStatus(orgId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: LeadDisplayStatus }) => {
      const { error } = await supabase.from('leads').update({ status }).in('id', ids);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
    },
  });
}

export interface LeadActivityRow {
  id: string;
  action: string;
  created_at: string;
  user_id: string | null;
}

async function fetchLeadActivity(orgId: string, leadId: string): Promise<LeadActivityRow[]> {
  const { data, error } = await supabase
    .from('audit_log')
    .select('id, action, created_at, user_id')
    .eq('organization_id', orgId)
    .eq('entity_type', 'leads')
    .eq('entity_id', leadId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return data;
}

export function useLeadActivity(orgId: string | null, leadId: string | null) {
  return useQuery({
    queryKey: ['lead-activity', orgId, leadId] as const,
    queryFn: () => {
      if (!orgId || !leadId) return Promise.resolve<LeadActivityRow[]>([]);
      return fetchLeadActivity(orgId, leadId);
    },
    enabled: Boolean(orgId && leadId),
    staleTime: 15 * 1000,
  });
}

export function useMarkLeadAsLost(orgId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, reason, prevNotes }: { leadId: string; reason: string; prevNotes: string | null }) => {
      const stamped = `[Lost: ${reason}]${prevNotes ? `\n\n${prevNotes}` : ''}`;
      const { error } = await supabase
        .from('leads')
        .update({ status: 'disqualified', notes: stamped })
        .eq('id', leadId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
    },
  });
}

export function useBulkSoftDeleteLeads(orgId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      await softDelete('leads', ids);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
    },
  });
}

interface ConvertLeadInput {
  lead_id: string;
  project_name: string;
  value_amount: number | null;
  /** If null, the lead's company_name is reused — otherwise this id wins. */
  customer_id?: string | null;
}

/**
 * Convert a lead into a real customer record + an open project.
 *
 * Post-0009 flow:
 *   1. Resolve or create a `customers` (companies) row for the lead.
 *   2. Resolve or create a primary `contacts` row for the lead.
 *   3. Insert a `projects` row with status='quote', linked to the customer +
 *      lead. (The Quote entity will get its own dedicated wizard later — for
 *      now the project IS the quote until it's accepted.)
 *   4. Update the lead: status='converted', linked_customer_id, linked_quote_id
 *      (set to project.id as a placeholder until quotes UI lands).
 */
export function useConvertLeadToCustomer(orgId: string | null, userId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ lead_id, project_name, value_amount, customer_id }: ConvertLeadInput) => {
      if (!orgId || !userId) throw new Error('Missing convert context');

      // Load the lead so we know the company_name + email + phone for resolve-or-create.
      const { data: lead, error: leadFetchErr } = await supabase
        .from('leads')
        .select('id, full_name, company_name, email, phone')
        .eq('id', lead_id)
        .eq('organization_id', orgId)
        .single();
      if (leadFetchErr) throw leadFetchErr;

      // Resolve customer (companies) — find by name (case-insensitive) or create.
      let resolvedCustomerId = customer_id ?? null;
      if (!resolvedCustomerId && lead.company_name) {
        const { data: existing } = await supabase
          .from('companies')
          .select('id')
          .eq('organization_id', orgId)
          .ilike('name', lead.company_name)
          .is('deleted_at', null)
          .limit(1)
          .maybeSingle();
        if (existing?.id) {
          resolvedCustomerId = existing.id;
        }
      }
      if (!resolvedCustomerId) {
        const { data: created, error: customerErr } = await supabase
          .from('companies')
          .insert({
            organization_id: orgId,
            name: lead.company_name?.trim() ? lead.company_name.trim() : lead.full_name,
            color: 'yellow',
          })
          .select('id')
          .single();
        if (customerErr) throw customerErr;
        resolvedCustomerId = created.id;
      }

      // Resolve or create primary contact from the lead's email/phone.
      let primaryContactId: string | null = null;
      if (lead.email || lead.phone) {
        const { data: existingContact } = await supabase
          .from('contacts')
          .select('id')
          .eq('organization_id', orgId)
          .eq('company_id', resolvedCustomerId)
          .is('deleted_at', null)
          .or(
            [
              lead.email ? `email.eq.${lead.email}` : null,
              lead.phone ? `phone.eq.${lead.phone}` : null,
            ]
              .filter(Boolean)
              .join(','),
          )
          .limit(1)
          .maybeSingle();
        if (existingContact?.id) {
          primaryContactId = existingContact.id;
        } else {
          const { data: createdContact } = await supabase
            .from('contacts')
            .insert({
              organization_id: orgId,
              company_id: resolvedCustomerId,
              full_name: lead.full_name,
              email: lead.email,
              phone: lead.phone,
            })
            .select('id')
            .single();
          primaryContactId = createdContact?.id ?? null;
        }
      }

      // Create the project (status='quote' is the new "deal-in-flight" state).
      const { data: project, error: projectErr } = await supabase
        .from('projects')
        .insert({
          organization_id: orgId,
          name: project_name.trim(),
          status: 'quote',
          company_id: resolvedCustomerId,
          primary_contact_id: primaryContactId,
          source_lead_id: lead_id,
          value_amount: value_amount ?? 0,
          value_currency: 'ILS',
          owned_by: userId,
          created_by: userId,
        })
        .select('id')
        .single();
      if (projectErr) throw projectErr;

      // Update the lead: archive it as converted, link to the customer + project.
      const { error: leadUpdateErr } = await supabase
        .from('leads')
        .update({
          status: 'converted',
          linked_customer_id: resolvedCustomerId,
          linked_quote_id: project.id, // Placeholder until QuoteWizard ships
        })
        .eq('id', lead_id)
        .eq('organization_id', orgId);
      if (leadUpdateErr) throw leadUpdateErr;

      return {
        customer_id: resolvedCustomerId,
        project_id: project.id,
        contact_id: primaryContactId,
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leads', orgId] });
      await queryClient.invalidateQueries({ queryKey: ['companies', orgId] });
      await queryClient.invalidateQueries({ queryKey: ['projects', orgId] });
    },
  });
}
